import React, { useState, useEffect, useRef } from "react";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { fetchData } from "../../utility/adminApi";
import Pagination from "../../components/Pagination";
import loader from "../../assets/loader.webp";
import ExportSalesList from "./ExportSalesList";
import SalesTableBody from "./SalesTableBody";
import { useQueryClient } from "@tanstack/react-query";
import SalesFilter from "./SalesFIlter";

function Sales() {
  const queryClient = useQueryClient();
  const isFirstRun = useRef(true);

  const [ordersData, setOrdersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const [searchData, setSearchData] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filteredData, setFilteredData] = useState("");

  const handleChange = (e) => {
    setSearchData(e.target.value);
  };

  const fetchOrders = async (page = 1, search = "", form = filteredData) => {
    const cacheKey = ["orders", page, search, form];
    const cachedData = queryClient.getQueryData(cacheKey);

    if (cachedData) {
      setOrdersData(cachedData?.data);
      setTotalPage(cachedData?.last_page);
      setIsLoading(false);
      return;
    }

    const response = await fetchData("orders", page, search, form);
    queryClient.setQueryData(cacheKey, response?.data);
    setOrdersData(response?.data?.data || []);
    setTotalPage(response?.data?.last_page);
    setIsLoading(false);
    setIsNextLoading(false);
    setIsPrevLoading(false);
  };

  // Initial load and page changes
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchOrders(currentPage, searchData);
    } else {
      fetchOrders(currentPage, searchData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setIsSearchLoading(true);
      setFilteredData(false);
      setCurrentPage(1);

      fetchOrders(1, searchData).then(() => {
        setIsSearchLoading(false);
      });
    }, 500);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  const handleCurrentPage = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowModal(true);
    setSearchData("");
  };

  if (isLoading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <img src={loader} alt="loader" className="w-10 lg:w-20" />
      </div>
    );
  }

  return (
    <div className="lg:overflow-y-hidden">
      {/* Header */}
      <div className="flex justify-between mb-4 items-center">
        <h3 className="text-xl font-semibold flex items-center gap-1">
          <MonetizationOnIcon />
          Sales
        </h3>
        <div className="flex flex-wrap">
          <ExportSalesList />
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white flex flex-col w-full lg:gap-3 overflow-x-auto items-center py-6 px-8">
        <div className="flex flex-col lg:flex-row justify-between items-center w-full mb-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="border-2 border-gray-400 w-60 p-2 px-6"
              value={searchData}
              onChange={handleChange}
              placeholder="Search"
            />
          </div>
          <button
            className="ml-2 flex items-center gap-1 hover:cursor-pointer border-black border px-4 py-2 rounded"
            onClick={handleFilter}
            aria-label="Filter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              width="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
              <circle cx="8" cy="6" r="2" />
              <circle cx="16" cy="12" r="2" />
              <circle cx="8" cy="18" r="2" />
            </svg>
            {/* Filter */}
          </button>
        </div>

        {/* Table */}
        {isSearchLoading ? (
          <div className="w-full h-screen flex justify-center items-center">
            <img src={loader} alt="loader" className="w-10 lg:w-10" />
          </div>
        ) : (
          <div className="w-full">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr className="bg-gray-200">
                  <th className="py-2 px-4 border-b border-gray-300">
                    ORDER NO
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300">
                    CUSTOMER NAME
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300">
                    CASHIER NAME
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300">
                    TOTAL PRICE
                  </th>
                  <th className="py-2 px-4 border-b border-gray-300">DATE</th>
                  <th className="py-2 px-4 border-b border-gray-300">STATUS</th>
                  <th className="py-2 px-4 border-b border-gray-300">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <SalesTableBody ordersData={ordersData} />
            </table>

            {/* Pagination */}
            <Pagination
              onPageChange={handleCurrentPage}
              totalPage={totalPage}
              currentPage={currentPage}
              setIsNextLoading={setIsNextLoading}
              setIsPrevLoading={setIsPrevLoading}
              isPrevLoading={isPrevLoading}
              isNextLoading={isNextLoading}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <SalesFilter
        isOpen={showModal}
        setOrdersData={setOrdersData}
        setCurrentPage={setCurrentPage}
        setTotalPage={setTotalPage}
        onClose={() => setShowModal(false)}
        currentPage={currentPage}
        fetchOrders={fetchOrders}
        setFilteredData={setFilteredData}
        setShowModal={setShowModal}
        filteredData={filteredData}
      />
    </div>
  );
}

export default Sales;
